import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { ScheduleKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import isEqual from 'lodash-es/isEqual';
import { Icon } from '@frontend/icons';
import { MultiLocationAccordionControl } from '@frontend/multi-location-accordion-control';
import { useAppScopeStore } from '@frontend/scope';
import { Accordion, ContentLoader, useModalControl } from '@frontend/design-system';
import { ScheduleInstructionBulkEditModal } from './schedule-instruction-bulk-edit-modal';
import { SchedulingInstructionAccordionForm } from './scheduling-instruction-accordion-form';
import { SCHEDULING_MODES, SchedulingMode } from './types';

type Props = {
  locationIds: string[];
  settings: Record<string, ScheduleKnowledge>;
  initialSettings: Record<string, ScheduleKnowledge>;
  isLoading?: boolean;
  onChange: (locationId: string, newMode: SchedulingMode) => void;
};

export const MultiLocationSchedulingInstruction: FC<Props> = ({
  locationIds,
  settings,
  initialSettings,
  isLoading,
  onChange,
}) => {
  const [filteredLocationIds, setFilteredLocationIds] = useState<string[]>(locationIds);
  const manageAllModalProps = useModalControl();
  const { getLocationName } = useAppScopeStore();

  return (
    <MultiLocationAccordionControl>
      <MultiLocationAccordionControl.Header
        filteredLocationIds={filteredLocationIds}
        openModal={manageAllModalProps.triggerProps.onClick}
        setFilteredLocationIds={setFilteredLocationIds}
        manageAllButtonProps={{
          disabled: isLoading,
        }}
      />

      <MultiLocationAccordionControl.Accordion
        startingValue={filteredLocationIds[0]}
        createAccordionBody={(locationId: string) => {
          const isDirty = !isEqual(settings[locationId]?.schedulingType, initialSettings[locationId]?.schedulingType);

          return (
            <Accordion.Item value={locationId}>
              <Accordion.Header css={styles.accordionHeader}>
                <Accordion.Header.Location title={getLocationName(locationId)} className='location-header' />
                {isDirty && <Icon name='alert-filled' color='warn' />}
              </Accordion.Header>
              <Accordion.Body
                css={css`
                  padding: var(--fabric-space-padding-lg);
                  position: relative;
                `}
              >
                <ContentLoader show={isLoading} />
                <SchedulingInstructionAccordionForm
                  mode={(settings[locationId]?.schedulingType as SchedulingMode) || SCHEDULING_MODES.DO_NOT_HELP}
                  onChange={(newMode: SchedulingMode) => onChange(locationId, newMode)}
                />
              </Accordion.Body>
            </Accordion.Item>
          );
        }}
        filteredLocationIds={filteredLocationIds}
      />

      {manageAllModalProps.modalProps.show && (
        <ScheduleInstructionBulkEditModal
          key={manageAllModalProps.modalProps.show ? 'open' : 'closed'}
          modalControls={manageAllModalProps}
          filteredLocationIds={filteredLocationIds}
        />
      )}
    </MultiLocationAccordionControl>
  );
};

const styles = {
  accordionHeader: css`
    .header-container {
      padding: var(--fabric-space-gap-md) 0px var(--fabric-space-gap-md) var(--fabric-space-gap-md);
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: var(--fabric-space-gap-sm);

      .location-header {
        justify-self: start;
      }
    }
  `,
};
